import { useState } from 'react';
import {
  Eye,
  EyeOff,
  Fuel,
  Lock,
  Mail,
  ShieldCheck,
  Truck,
  Zap,
} from 'lucide-react';
import { DEMO_CREDENTIALS, writeSession } from '../../utils/authSession';
import { P } from '../../theme/logisticsTheme';

const inputClass =
  'w-full rounded-xl border px-4 py-3 text-sm font-medium text-black outline-none transition focus:ring-2';

export default function LoginPage({ onLoginSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    window.setTimeout(() => {
      const session = {
        email: DEMO_CREDENTIALS.email,
        displayName: DEMO_CREDENTIALS.displayName,
        role: 'Operations Admin',
        loggedInAt: new Date().toISOString(),
      };

      writeSession({ ...session, remember });
      onLoginSuccess(session);
      setLoading(false);
    }, 450);
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4 sm:p-6 lg:p-8"
      style={{ backgroundColor: P.silver, color: P.text, colorScheme: 'light' }}
    >
      <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border shadow-lg lg:grid-cols-[1.05fr_1fr]" style={{ borderColor: P.grid, backgroundColor: P.white }}>
        {/* Brand panel */}
        <div
          className="relative hidden flex-col justify-between border-r p-8 text-black lg:flex xl:p-10"
          style={{
            backgroundColor: P.silver,
            borderColor: P.grid,
            color: P.text,
          }}
        >
          <div>
            <div className="mb-8 flex items-center gap-3">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm"
                style={{ backgroundColor: P.blueTint, boxShadow: `inset 0 0 0 1px ${P.grid}` }}
              >
                <Zap className="h-6 w-6" style={{ color: P.blue }} strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-lg font-black tracking-tight text-black">
                  OGTS LogiCore<span style={{ color: P.blue }}>™</span>
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-black/60">
                  Logistics Command Center
                </p>
              </div>
            </div>

            <h1 className="text-3xl font-bold leading-tight text-black xl:text-4xl">
              UAE Oil &amp; Gas Logistics ERP
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-black/70">
              Fleet dispatch, terminal operations, vendor settlements, and B2B client portals —
              managed from one secure command center.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { icon: Truck, label: '24/7 Fleet & Dispatch Monitoring' },
              { icon: Fuel, label: 'Terminal & Vendor Network Control' },
              { icon: ShieldCheck, label: 'Enterprise-grade Secure Access' },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-xl border px-4 py-3"
                style={{ backgroundColor: P.white, borderColor: P.grid }}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: P.blue }} strokeWidth={1.75} />
                <span className="text-sm font-semibold text-black">{label}</span>
              </div>
            ))}
          </div>

          <p className="text-[11px] font-medium text-black/50">
            Mockup environment · Phase 1 Blueprint v1.0
          </p>
        </div>

        {/* Login form */}
        <div className="flex flex-col justify-center p-6 sm:p-8 xl:p-10">
          <div className="mb-6 lg:hidden">
            <div className="mb-3 flex items-center gap-2">
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ background: `linear-gradient(135deg, ${P.blue}, #2563EB)` }}
              >
                <Zap className="h-5 w-5 text-white" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-sm font-black" style={{ color: P.text }}>
                  OGTS LogiCore™
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: P.subtle }}>
                  Sign in to continue
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-black">Welcome back</h2>
            <p className="mt-1 text-sm" style={{ color: P.muted }}>
              Sign in to access your operations dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-black/70">
                Work Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/40" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  readOnly
                  value={DEMO_CREDENTIALS.email}
                  className={`${inputClass} cursor-default pl-10`}
                  style={{ borderColor: P.grid, backgroundColor: P.silver }}
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-black/70">
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/40" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  readOnly
                  value={DEMO_CREDENTIALS.password}
                  className={`${inputClass} cursor-default pl-10 pr-11`}
                  style={{ borderColor: P.grid, backgroundColor: P.silver }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-black/45 hover:text-black"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-black/70">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-logi-grid text-logi-accent focus:ring-logi-accent"
                />
                Remember session
              </label>
              <button type="button" className="text-sm font-semibold hover:underline" style={{ color: P.blue }}>
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl border py-3 text-sm font-bold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                backgroundColor: P.btnBlue,
                borderColor: P.btnBlueBorder,
              }}
            >
              {loading ? 'Signing in…' : 'Sign In to LogiCore'}
            </button>
          </form>

          <div
            className="mt-6 rounded-xl border p-4"
            style={{ backgroundColor: P.blueTint, borderColor: P.btnBlueBorder }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: P.blue }}>
              Demo credentials (mockup)
            </p>
            <p className="mt-2 font-mono text-sm font-semibold text-black">
              {DEMO_CREDENTIALS.email}
            </p>
            <p className="font-mono text-sm font-semibold text-black">
              Password: {DEMO_CREDENTIALS.password}
            </p>
            <p className="mt-3 text-xs font-medium" style={{ color: P.muted }}>
              Mockup login — credentials are pre-filled above.
            </p>
          </div>

          <p className="mt-6 text-center text-[11px]" style={{ color: P.subtle }}>
            Secure B2B portal · UAE Oil &amp; Gas Logistics · OGTS LogiCore™
          </p>
        </div>
      </div>
    </div>
  );
}
